import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI.jsx";
import useColors from "../../../../../api/hooks/useColors.jsx";
import { notificationSettingsKeys } from "../../../../../api/queryKeys.js";
import LoadingPrimaryButton from "../../../../../components/buttons/LoadingPrimaryButton.jsx";
import LoadingDeletableChip from "../../../../../components/chips/LoadingDeletableChip.jsx";
import CustomToolTip from "../../../../../components/CustomTooltip.jsx";
import { Toast } from "../../../../../components/Toast.jsx";
import slack_channel_id from "../../../../stack/integrations/Slack/images/slack_channel_id.jpg";
import { field_IDs_mapping } from "../../fieldMapping.jsx";
import { SLACK_TOOL } from "./GeneralNotificationsPage.jsx";
import SuccessfulAddedChannelModal from "./SuccessfulAddedChannelModal.jsx";

export default function AddSlackChannelModal({
  isOpen,
  setIsOpen,
  channels,
  setChannels,
}) {
  const colors = useColors();
  const [channelID, setChannelID] = useState("");
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    getSlackChannelInfo,
    trackEvent,
    updateNotificationSetting,
    removeSlackChannel,
  } = useAPI();

  const queryClient = useQueryClient();

  const addChannel = async () => {
    const filteredChannels = channels.filter(
      (channel) => channel.id === channelID
    );
    if (filteredChannels.length > 0) {
      Toast.error(`Channel ${filteredChannels[0].name} already added`);
      setChannelID("");
      return;
    }

    trackEvent(
      `User is adding slack channel ${channelID} in the notification center`
    ).then();

    try {
      const channelInfo = await getSlackChannelInfo(channelID);
      const newChannel = {
        id: channelID,
        name: channelInfo["data"]["name"],
        selected: true,
        verified: true,
      };
      const newValue = [...channels, newChannel];
      await updateNotificationSetting(
        SLACK_TOOL,
        field_IDs_mapping.SLACK.SLACK_CHANNELS,
        newValue
      );
      setChannels(newValue);
      setShowSuccessModal(true);
    } catch (error) {
      if (error.response.status === 404)
        Toast.error(`Channel with ID ${channelID} not found`);
      else Toast.error("Error adding channel");
    }
  };

  const deleteChannel = async (channelToRemove) => {
    trackEvent(
      `User is removing slack channel ${channelToRemove.name} (${channelToRemove.id}) from the notifications center`
    ).then();

    const newValue = channels.filter(
      (channel) => channel.id !== channelToRemove.id
    );

    try {
      await removeSlackChannel(channelToRemove.id);
      setChannels(newValue);
      queryClient.invalidateQueries({
        queryKey: notificationSettingsKeys.all,
      });
      Toast.success(`Channel ${channelToRemove.name} removed successfully`);
    } catch {
      Toast.error("Error deleting channel");
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{ sx: { minWidth: "700px" } }}
      >
        <DialogTitle>Manage Slack Channels</DialogTitle>
        <DialogContent>
          <Grid container direction="column" gap={2}>
            <Grid item>
              <Typography variant="subtitle2">Add Channel ID</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">
                Enter the channel and click the channel name (on the top of the
                thread). <br />
                The Channel ID is located on the bottom.
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2">
                For a <span style={{ fontWeight: 600 }}>private channel</span>,
                type and send{" "}
                <code
                  style={{
                    backgroundColor: colors.grey_200,
                    padding: "2px 5px",
                    borderRadius: "4px",
                    fontFamily: "Monaco, monospace",
                    whiteSpace: "nowrap",
                  }}
                >
                  /invite @Breeze
                </code>{" "}
                in the channel.
              </Typography>
            </Grid>
            <Grid item>
              <Accordion
                expanded={accordionExpanded}
                onChange={() => setAccordionExpanded((prev) => !prev)}
                sx={{ backgroundColor: `${colors.sidebar_bg}` }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="overline" color={colors.primary_darker}>
                    Show example
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <img
                    src={slack_channel_id}
                    alt="slack id screenshot"
                    style={{ width: "600px" }}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item>
              <Grid container direction="row" gap={2} alignItems="center">
                <Grid item width="50%">
                  <TextField
                    id="scan channel id"
                    placeholder="Channel ID"
                    value={channelID}
                    fullWidth
                    onChange={(event) => {
                      setChannelID(event.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "30px",
                        "& .MuiInputBase-input": {
                          height: "36px",
                          padding: "0 14px",
                        },
                      },
                    }}
                    InputProps={{
                      style: {
                        fontSize: "0.875rem",
                      },
                    }}
                    inputProps={{
                      style: {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <LoadingPrimaryButton
                    size="small"
                    disabled={!channelID}
                    onClick={async () => {
                      if (channelID) await addChannel();
                    }}
                  >
                    Add
                  </LoadingPrimaryButton>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" gap={1}>
                {channels.map((channel) =>
                  channel.verified ? (
                    <LoadingDeletableChip
                      label={channel.name}
                      onDelete={() => deleteChannel(channel)}
                      key={channel.id}
                    />
                  ) : (
                    <CustomToolTip title="Unreachable channel. Try to remove and add again">
                      <Box>
                        <LoadingDeletableChip
                          label={channel.name}
                          onDelete={() => deleteChannel(channel)}
                          key={channel.id}
                          sx={{ color: colors.error_48percent }}
                        />
                      </Box>
                    </CustomToolTip>
                  )
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <SuccessfulAddedChannelModal
        isOpen={showSuccessModal}
        setIsOpen={setShowSuccessModal}
        channelID={channelID}
        setChannelID={setChannelID}
      />
    </>
  );
}
